import { capitalize } from '@mui/material';
import {
  BSC_CHAIN_NAME,
  mapChainName,
  Chain,
  ChainID,
  TAC_CHAINS,
} from '@ankr.com/chains-list';

import { ADVANCED_API_PATH } from 'domains/advancedApi/routes';
import {
  TAC_SPB_TENDERMINT_NAME,
  TAC_TENDERMINT_NAME,
} from 'modules/chains/constants';
import { isBeacon } from 'modules/chains/utils/isBeacon';

const renderPrefix = (name: ChainID) => {
  let renderedName = name as string;

  if (name.includes(ChainID.BSC)) {
    renderedName = name.replace(ChainID.BSC, BSC_CHAIN_NAME);
  }

  return renderedName;
};

export const renderNervosName = (chainId: ChainID) => {
  let name = capitalize(chainId);

  if (chainId === ChainID.NERVOS_CKB) {
    name = 'Nervos CKB';
  } else if (chainId === ChainID.NERVOS_GW) {
    name = 'Nervos Godwoken ';
  }

  return name;
};

const renderSecretName = (chainId: ChainID) => {
  let name = capitalize(chainId);

  if (chainId === ChainID.SECRET) {
    name = 'Secret';
  } else if (chainId === ChainID.SECRET_REST) {
    name = 'Secret Tendermint REST';
  } else if (chainId === ChainID.SECRET_COSMOS_REST) {
    name = 'Secret Cosmos REST';
  }

  return name;
};

const renderSeiName = (chainId: ChainID) => {
  const name = capitalize(chainId);

  const namesMap: Partial<Record<ChainID, string>> = {
    [ChainID.SEI]: 'Sei',
    [ChainID.SEI_COSMOS_REST]: 'Sei Cosmos REST',
    [ChainID.SEI_REST]: 'Sei Tendermint REST',
    [ChainID.SEI_RPC]: 'Sei Tendermint JSON-RPC',
    [ChainID.SEI_TENDERMINT_GRPC]: 'Sei Tendermint gRPC',
  };

  return namesMap[chainId] || name;
};

export const renderTacName = (chainId: ChainID) => {
  const name = capitalize(chainId);

  const namesMap: Partial<Record<ChainID, string>> = {
    [ChainID.TAC]: 'TAC',
    [ChainID.TAC_EVM]: 'TAC EVM JSON-RPC',
    [ChainID.TAC_TENDERMINT_JSON_RPC]: 'TAC Tendermint JSON-RPC',
    [ChainID.TAC_TENDERMINT_REST]: TAC_TENDERMINT_NAME,
    [ChainID.TAC_COSMOS_REST]: 'TAC Cosmos REST',
    [ChainID.TAC_COSMOS_GRPC_WEB]: 'TAC Cosmos gRPC-web',
    [ChainID.TAC_TESTNET_SPB]: 'TAC SPB',
    [ChainID.TAC_TESTNET_SPB_GRPC]: 'TAC SPB gRPC',
    [ChainID.TAC_TESTNET_SPB_COSMOS_GRPC]: 'TAC SPB Cosmos gRPC-web',
    [ChainID.TAC_TESTNET_SPB_COSMOS_REST]: 'TAC SPB Cosmos REST',
    [ChainID.TAC_TESTNET_SPB_EVM]: 'TAC SPB EVM JSON-RPC',
    [ChainID.TAC_TESTNET_SPB_TENDERMINT_REST]: TAC_SPB_TENDERMINT_NAME,
    [ChainID.TAC_TESTNET_SPB_TENDERMINT_RPC]: 'TAC SPB Tendermint JSON-RPC',
  };

  return namesMap[chainId] || name;
};

const renderETHName = (chainId: ChainID) => {
  let name = 'Ethereum';

  if (chainId === ChainID.ETH_RINKEBY) {
    name = 'Rinkeby Testnet';
  } else if (chainId === ChainID.ETH_ROPSTEN) {
    name = 'Ropsten Testnet';
  } else if (chainId === ChainID.ETH_SEPOLIA) {
    name = 'Sepolia Testnet';
  } else if (chainId === ChainID.ETH_HOLESKY) {
    name = 'Holesky Testnet';
  } else if (chainId === ChainID.ETH_HOODI) {
    name = 'Hoodi Testnet';
  }

  return name;
};

const getSubchainName = (chainId: string) => {
  const index = chainId.indexOf('-');
  const mainChainName = chainId.substring(0, index);
  const subChainName = chainId.substring(index + 1);

  return `${capitalize(mainChainName)}-${capitalize(subChainName)}`;
};

const getTestnetChainName = (chainId: string) => {
  let name = chainId;
  let index = name.indexOf('_');
  let mainnetName: string;
  let testnetName: string;
  let testnetChainName = '';
  let prefix = '';

  while (index > -1) {
    mainnetName = name.substring(0, index);
    testnetName = name.substring(index + 1);
    prefix += `${capitalize(mainnetName)} `;

    if (testnetName.includes('-')) {
      testnetName = getSubchainName(testnetName);
    }

    name = testnetName;
    index = testnetName.indexOf('_');
    testnetChainName = `${prefix}${capitalize(testnetName)}`;
  }

  return testnetChainName;
};

const renderZetaChainName = (chainId: ChainID) => {
  let name = 'ZetaChain Testnet';

  if (chainId === ChainID.ZETACHAIN_COSMOS_REST_ATHENS_TESTNET) {
    name = 'ZetaChain Athens-3 Cosmos REST Testnet';
  } else if (chainId === ChainID.ZETACHAIN_TENDERMINT_REST_ATHENS_TESTNET) {
    name = 'ZetaChain Athens-3 Tendermint REST Testnet';
  } else if (chainId === ChainID.ZETACHAIN_TENDERMINT_RPC_ATHENS_TESTNET) {
    name = 'ZetaChain Athens-3 Tendermint JSON-RPC Testnet';
  } else if (chainId === ChainID.ZETACHAIN_EVM_ATHENS_TESTNET) {
    name = 'ZetaChain Athens-3 EVM RPC Testnet';
  }

  return name;
};

const renderZkevmName = (chainId: ChainID) => {
  let name = 'Polygon zkEVM';

  if (chainId === ChainID.POLYGON_ZKEVM_TESTNET) {
    name = `${name} Testnet`;
  }

  if (chainId === ChainID.POLYGON_ZKEVM_CARDONA) {
    name = `${name} Cardona Testnet`;
  }

  return name;
};

const renderIotexName = (chainId: ChainID) => {
  let name = 'IoTex';

  if (chainId === ChainID.IOTEX_TESTNET) {
    name = `${name} Testnet`;
  }

  return name;
};

const renderHecoName = (chainId: ChainID) => {
  let name = 'Huobi ECO Chain';

  if (chainId === ChainID.HECO_TESTNET) {
    name = `${name} Testnet`;
  }

  return name;
};

/* eslint-disable max-lines-per-function */
export const getChainName = (chainId: ChainID, beacons: Chain[] = []) => {
  let name = capitalize(chainId);

  name = mapChainName(chainId, name);

  if (chainId === ChainID.ZKSYNC_ERA) {
    name = 'zkSync Era';
  } else if (chainId === ChainID.OPTIMISM_TESTNET) {
    name = 'Optimism Goerli';
  } else if (chainId.includes(ChainID.POLYGON_ZKEVM)) {
    name = renderZkevmName(chainId);
  } else if (chainId.includes(ChainID.IOTEX)) {
    name = renderIotexName(chainId);
  } else if (chainId.includes(ChainID.HECO)) {
    name = renderHecoName(chainId);
  } else if (chainId.includes(ChainID.NERVOS)) {
    name = renderNervosName(chainId);
  } else if (chainId.includes(ChainID.SECRET)) {
    name = renderSecretName(chainId);
  } else if (chainId.includes(ChainID.SEI)) {
    name = renderSeiName(chainId);
  } else if (
    chainId.includes(ChainID.ETH) &&
    !chainId.includes(ChainID.ETHERLINK)
  ) {
    name = renderETHName(chainId);
  } else if (chainId.includes(ChainID.HORIZEN_TESTNET)) {
    name = 'Horizen EON Testnet';
  } else if (chainId.includes(ChainID.HORIZEN)) {
    name = 'Horizen EON';
  } else if (chainId.includes(ChainID.TENET)) {
    name = 'Tenet EVM';
  } else if (chainId.includes(ChainID.SCROLL_SEPOLIA_TESTNET)) {
    name = 'Scroll Sepolia Testnet';
  } else if (chainId.includes(ChainID.SCROLL_TESTNET)) {
    name = 'Scroll Testnet';
  } else if (ADVANCED_API_PATH.includes(chainId)) {
    name = 'Advanced API';
  } else if (chainId === ChainID.ARBITRUM_NOVA) {
    name = 'Arbitrum Nova';
  } else if (chainId.includes(ChainID.ZETACHAIN)) {
    name = renderZetaChainName(chainId);
  } else if (chainId === ChainID.TRON_JSON_RPC) {
    name = 'TRON JSON-RPC';
  } else if (chainId === ChainID.BASE) {
    name = 'Base';
  } else if (chainId === ChainID.XDC) {
    name = 'XDC Network';
  } else if (chainId === ChainID.XLAYER) {
    name = 'X Layer';
  } else if (chainId === ChainID.XLAYER_TESTNET) {
    name = 'X Layer Testnet';
  } else if (chainId === ChainID.STELLAR_TESTNET_HORIZON) {
    name = 'Stellar Horizon Testnet';
  } else if (chainId === ChainID.STELLAR_SOROBAN) {
    name = 'Stellar RPC';
  } else if (chainId === ChainID.STELLAR_TESTNET_SOROBAN) {
    name = 'Stellar RPC Testnet';
  } else if (chainId.includes(ChainID.BERACHAIN_GUARDED_TESTNET)) {
    name = 'Berachain Guarded Testnet';
  } else if (chainId === ChainID.KAVA_COSMOS_REST) {
    name = 'Kava HTTP REST';
  } else if (chainId === ChainID.KAVA) {
    name = 'Kava';
  } else if (chainId === ChainID.KAVA_EVM) {
    name = 'Kava EVM JSON-RPC';
  } else if (chainId === ChainID.KAVA_TENDERMINT_REST) {
    name = 'Kava Tendermint Rest';
  } else if (chainId === ChainID.KAVA_TENDERMINT_RPC) {
    name = 'Kava JSON-RPC';
  } else if (chainId === ChainID.KAVA_TENDERMINT_GRPC) {
    name = 'Kava Tendermint gRPC';
  } else if (chainId === ChainID.KAVA_COSMOS_REST_TESTNET) {
    name = 'HTTP Rest Testnet';
  } else if (chainId === ChainID.KAVA_EVM_TESTNET) {
    name = 'Kava EVM JSON-RPC Testnet';
  } else if (chainId === ChainID.KAVA_TENDERMINT_REST_TESTNET) {
    name = 'Kava Tendermint Rest Testnet';
  } else if (chainId === ChainID.KAVA_TENDERMINT_RPC_TESTNET) {
    name = 'Kava JSON-RPC Testnet';
  } else if (chainId === ChainID.ZERO_G) {
    name = '0G';
  } else if (chainId === ChainID.ZERO_G_EVM) {
    name = '0G EVM JSON-RPC';
  } else if (chainId === ChainID.ZERO_G_BEACONKIT_REST) {
    name = '0G Beaconkit Rest';
  } else if (chainId === ChainID.ZERO_G_GALILEO_TESTNET) {
    name = '0G Galileo';
  } else if (chainId === ChainID.ZERO_G_GALILEO_EVM) {
    name = '0G Galileo EVM JSON-RPC';
  } else if (chainId === ChainID.ZERO_G_GALILEO_BEACONKIT_REST) {
    name = '0G Galileo Beaconkit Rest';
  } else if (chainId === ChainID.ZERO_G_GALILEO_TENDERMINT_REST) {
    name = '0G Galileo Tendermint Rest';
  } else if (chainId === ChainID.ZERO_G_GALILEO_TENDERMINT_RPC) {
    name = '0G Galileo JSON-RPC';
  } else if (chainId === ChainID.BTC) {
    name = 'Bitcoin';
  } else if (chainId === ChainID.TON) {
    name = 'TON';
  } else if (chainId === ChainID.FUEL) {
    name = 'Fuel Mainnet';
  } else if (chainId === ChainID.FUEL_REST) {
    name = 'Fuel Mainnet';
  } else if (chainId === ChainID.FUEL_SEPOLIA) {
    name = 'Fuel Sepolia Testnet';
  } else if (chainId === ChainID.CORN_MAIZENET) {
    name = 'Corn Maizenet';
  } else if (chainId === ChainID.CORN_TESTNET) {
    name = 'Corn Testnet';
  } else if (chainId === ChainID.SWELL_SEPOLIA) {
    name = 'Swell Sepolia';
  } else if (chainId === ChainID.GOAT_TESTNET) {
    name = 'Goat Testnet';
  } else if (TAC_CHAINS.includes(chainId)) {
    name = renderTacName(chainId);
  } else if (chainId === ChainID.SONIC_BLAZE_TESTNET) {
    name = 'Sonic Blaze Testnet';
  } else if (chainId === ChainID.IOTA_EVM) {
    name = 'IOTA EVM';
  } else if (chainId === ChainID.IOTA_EVM_TESTNET) {
    name = 'IOTA EVM Testnet';
  } else if (chainId === ChainID.IOTA) {
    name = 'IOTA';
  } else if (chainId === ChainID.IOTA_TESTNET) {
    name = 'IOTA Testnet';
  } else if (chainId === ChainID.STORY_AENEID) {
    name = 'Story Aeneid Testnet';
  } else if (chainId === ChainID.MOVEMENT_MAINNET) {
    name = 'Movement';
  } else if (chainId === ChainID.NEURA_TESTNET) {
    name = 'Neura Testnet';
  } else if (chainId === ChainID.WHITECHAIN_TESTNET) {
    name = 'Whitechain Sepolia';
  } else if (chainId === ChainID.XRP) {
    name = 'XRP';
  } else if (
    chainId === ChainID.ZKVERIFY ||
    chainId === ChainID.ZKVERIFY_MAINNET
  ) {
    name = 'zkVerify';
  } else if (chainId === ChainID.ZKVERIFY_VFLOW_PARACHAIN_MAINNET) {
    name = 'zkVerify vFlow Mainnet';
  } else if (chainId === ChainID.ZKVERIFY_VFLOW_PARACHAIN_TESTNET) {
    name = 'zkVerify vFlow Parachain Testnet';
  } else if (chainId === ChainID.ZKVERIFY_VOLTA_TESTNET) {
    name = 'zkVerify Volta Testnet';
  } else if (chainId === ChainID.ETHERLINK_SHADOWNET_TESTNET) {
    name = 'Etherlink Shadownet Testnet';
  } else if (chainId === ChainID.ETHERLINK_TESTNET) {
    name = 'Etherlink Testnet';
  } else if (chainId === ChainID.SOMNIA_TESTNET) {
    name = 'Somnia Testnet';
  } else if (chainId === ChainID.ROBINHOOD) {
    name = 'Robinhood Chain';
  } else if (chainId === ChainID.WORLDCHAIN) {
    name = 'World Chain';
  } else if (chainId === ChainID.POLKADOT_ASSET_HUB) {
    name = 'Polkadot Asset Hub';
  } else if (chainId.includes('_') && name.includes('_')) {
    name = getTestnetChainName(renderPrefix(chainId));
  } else if (chainId.includes('-')) {
    name = getSubchainName(renderPrefix(chainId));
  }

  if (name.includes('testnet')) {
    name = name.replace('testnet', 'Testnet');
  }

  if (isBeacon(chainId)) {
    const beacon = beacons.find(({ id }) => id === chainId);

    name = beacon?.name || name;
  }

  return name;
};
